import React from 'react'
import { useInView } from 'react-hook-inview'
import { graphql } from 'gatsby'
import FAQ from '../components/FAQ'
import SiteHeader from '../components/SiteHeader'
import Navigation from '../components/Navigation'
import NavigationDropdown from '../components/NavigationDropdown'
import ArticleContainer from '../components/ArticleContainer'
import ArticleLayout from '../components/ArticleLayout'
import Breadcrumb from '../components/Breadcrumb'
import FAQNav from '../components/FAQNav'
import FAQSpy from '../components/FAQSpy'
import NotificationBanner from '../components/NotificationBanner'
import { usePageViewTracking } from '../core'
import { SEO } from '../components/SEO'
import BackToTop from '../components/ButtonToTop'

const ArticleMainLayout = props => {
  return (
    <main className="px-0 py-10 mr-auto xxl:px-3 lg:w-7/12 xl:w-6/12">
      {props.children}
    </main>
  )
}

const Page = ({ data: { faqPages } }) => {
  usePageViewTracking()

  const { Section_Name: category, PageUrl: slug, PageName, FAQs } = faqPages

  const [ref, isVisible, entry] = useInView({ rootMargin: '-150px' })
  return (
    <>
      <SiteHeader search sticky />
      <BackToTop />
      <NotificationBanner />
      <ArticleContainer>
        {entry && <FAQSpy faqs={FAQs} isVisible={isVisible} />}
        <ArticleLayout>
          <Navigation currentCategory={category} currentFaqPageUrl={slug} />
          <NavigationDropdown
            currentCategory={category}
            currentFaqPageUrl={slug}
          />
          <ArticleMainLayout>
            <Breadcrumb category={category} currentPage={PageName} />
            <h1 className="mb-8 text-3xl font-semibold lg:mb-12">{PageName}</h1>
            <div ref={ref}>
              <FAQNav faqs={FAQs} />
            </div>
            {(FAQs || []).map(
              (faq, index) =>
                faq && (
                  <FAQ
                    key={`FAQ-${index}`}
                    faq={faq}
                    index={index}
                    links={category}
                  />
                )
            )}
          </ArticleMainLayout>
        </ArticleLayout>
      </ArticleContainer>
    </>
  )
}

export const query = graphql`
  query($PageUrl: String) {
    faqPages(PageUrl: { eq: $PageUrl }) {
      PageUrl
      PageName
      Section_Name
      FAQs {
        Media_Url
        AnswerText
        ChangeDate
        Media_Type
        ReferenceID
        QuestionText
        ParentPageURL
        AdditionalNote
        FAQ_CTAs {
          TargetPath
          LinkCaption
          LinkCopy
        }
      }
    }
  }
`

export default Page

export const Head = ({ data: { faqPages } }) => {
  const { PageName, FAQs } = faqPages
  const generateFAQJsonLdData = () => {
    const mainEntity = FAQs.map(faq => ({
      '@type': 'Question',
      name: faq.QuestionText,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.AnswerText,
      },
    }))
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      name: `${PageName} FAQs`,
      mainEntity,
    })
  }
  return (
    <SEO
      title={`${PageName} - Morrisons Help Hub`}
      description={`Frequently asked questions related to ${PageName}`}
    >
      <script type="application/ld+json">{generateFAQJsonLdData()}</script>
    </SEO>
  )
}
